/*
TODO: Implement mobile code (incl. 3Pay, Onboarding Tooltips etc.)
SOURCE: screens/StatementScreen/StatementScreen/MonthlyChargesTabs.tsx
*/

import React, { useMemo, useRef, useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import {
  useConsumerUsageSummaryQuery,
  useMultiPayContractsQuery,
} from 'apollo/queries/autogenerate/hooks';
import {
  Charge,
  MultipayContractStatus,
} from 'apollo/queries/autogenerate/schemas';
import Icon from 'shared/modules/common/components/atoms/Icon';
import Tabs, { Tab } from 'shared/modules/common/components/atoms/Tabs';
import ChargeItem from 'shared/modules/common/components/organisms/ChargeItem';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import ListItemDivider from 'shared/modules/usage/components/atoms/ListItemDivider';
import Card from 'shared/modules/usage/components/molecules/Card';
import { formatMonthlyCharges } from 'utils/format-monthly-charges';

type Props = {
  onViewMultipayContractsPress: () => void;
  onChargeItemPress: (charge: Charge) => void;
};

export default function MonthlyChargesTabs({
  onViewMultipayContractsPress,
  onChargeItemPress,
}: Props) {
  const { data } = useConsumerUsageSummaryQuery();

  const monthlyCharges = useMemo(() => {
    return formatMonthlyCharges(
      data?.consumerUsageSummary.monthlyChargesBreakdown
    );
  }, [data]);

  const defaultTabIndex = monthlyCharges.length ? monthlyCharges.length - 1 : 0;
  const [activeTabIndex, setActiveTabIndex] = useState(defaultTabIndex);
  const tabsHeight = useRef(new Array(monthlyCharges.length).fill(0));
  // to rerender screen after saving heights in tabsHeight
  const [, setReady] = useState(false);

  // Check if the consumer has either active, approved, pending MultipayContracts
  const { data: contracts } = useMultiPayContractsQuery({
    variables: {
      status: [
        MultipayContractStatus.ACTIVE,
        MultipayContractStatus.APPROVED,
        MultipayContractStatus.PENDING,
      ],
    },
  });

  const hasMultiPay = contracts?.multipayContracts.length !== 0;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>ご利用履歴</Text>

      {!!monthlyCharges.length && (
        <Tabs
          style={styles.tabs}
          activeTabIndex={activeTabIndex}
          onTabChange={(index) => setActiveTabIndex(index)}
          id="monthly-charges-tabs"
        >
          {monthlyCharges.map((month, index) => (
            <Tab
              label={month.monthLabel}
              style={
                tabsHeight.current[activeTabIndex]
                  ? { height: tabsHeight.current[activeTabIndex] }
                  : {}
              }
              key={index}
            >
              <View
                style={styles.chargesListContainer}
                onLayout={(event) => {
                  // this is done not to lose height of tab
                  // tha is why it is saved to ref not to state
                  // --
                  // The number of charges per month are different
                  // it is causing an empty space under some tab
                  // to prevent the issue we save the height to dynamically
                  // change it for an active tab
                  tabsHeight.current[index] = event.nativeEvent.layout.height;

                  // to rerender screen after height of each tab is set
                  // saving in useRef doesn't rerender the page
                  if (index === monthlyCharges.length - 1) {
                    setReady(true);
                  }
                }}
              >
                <ChargesList
                  charges={month.charges as [Charge]}
                  onChargeItemPress={onChargeItemPress}
                  id={`monthly-charges-tabs-item-${index}`}
                />
              </View>
            </Tab>
          ))}
        </Tabs>
      )}
      {hasMultiPay && (
        <View style={styles.multipayContractContainer}>
          <TouchableOpacity
            onPress={onViewMultipayContractsPress}
            /** Generate HTML and Test IDs  */
            {...generateID(
              'monthly-charges-tabs-view-multipay-contracts-list-button'
            )}
            /** Platform-specific props */
            {...Platform.select({
              web: {
                accessibilityRole: 'button',
              },
            })}
          >
            <Text style={styles.multipayContractText}>分割払い契約の詳細</Text>
          </TouchableOpacity>
          <Icon size="16" name="caret-right" />
        </View>
      )}
    </View>
  );
}

type ChargesListProps = {
  charges: [Charge];
  onChargeItemPress: (charge: Charge) => void;
  id?: string;
};

const ChargesList = ({ charges, onChargeItemPress, id }: ChargesListProps) => {
  return (
    <Card style={styles.chargeTab}>
      {charges.length ? (
        <>
          {charges.map((charge, index) => (
            <View key={charge.id}>
              <ChargeItem
                charge={charge}
                onPress={onChargeItemPress}
                id={`${id}-list-item-${index}`}
              />
              {index !== charges.length - 1 && <ListItemDivider />}
            </View>
          ))}
        </>
      ) : (
        <View style={styles.messageContainer}>
          <Text style={styles.emptyMessage}>ご利用はありません。</Text>
        </View>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
  },
  chargeTab: {
    marginVertical: 16,
  },
  title: {
    ...typography.bodyBold,
    color: colors.teal,
    marginBottom: 16,
  },
  messageContainer: {
    alignItems: 'center',
  },
  emptyMessage: {
    ...typography.caption02,
    color: colors.greyMedium,
    margin: 22,
  },
  multipayContractContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  multipayContractText: {
    ...typography.caption01,
    fontSize: 13,
    fontWeight: '700',
    color: colors.greyDark,
    textAlign: 'right',
    marginStart: 4,
  },
  tabs: {
    marginHorizontal: -16,
  },
  chargesListContainer: {
    marginHorizontal: 16,
    marginTop: -12,
  },
});
